import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, ElementRef, HostListener, inject, input, output } from '@angular/core';
import { getTimeOptions, setCssValue } from '@lb/functions';

@Component({
	selector: 'lb-schedule-base-calendar',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './schedule-base-calendar.component.html',
	styleUrls: ['./schedule-base-calendar.component.scss']
})
export class ScheduleBaseCalendarComponent implements AfterViewInit {
	private readonly _elementRef = inject(ElementRef);

	selectedSchedules = input.required<string[]>();
	formStep = input<number>(-1);
	disableCalendar = input<boolean>(false);
	preferredTimeSlots = input<{ dayIndex: number; timeID: string }[]>([]);

	removeScheduleSelection = output<{ scheduleID: string; step: number }>();
	addScheduleSelection = output<{ scheduleData: { dayIndex: number; timeID: string }; step: number }>();

	private isDragging = false;
	private dragMode: 'add' | 'remove' | null = null;
	private readonly dragCells = new Set<string>();

	timesOfDay = computed<{ id: string; hour: number; minute: number; label: string }[]>(() => {
		return this.populateTimes(30);
	});

	daysOfWeek = computed<{ dayIndex: number; format: string }[]>(() => {
		return [
			{ dayIndex: 0, format: 'Sun' },
			{ dayIndex: 1, format: 'Mon' },
			{ dayIndex: 2, format: 'Tue' },
			{ dayIndex: 3, format: 'Wed' },
			{ dayIndex: 4, format: 'Thu' },
			{ dayIndex: 5, format: 'Fri' },
			{ dayIndex: 6, format: 'Sat' }
		];
	});

	// Optional: End drag if mouse leaves the calendar area
	@HostListener('document:mouseup', ['$event'])
	onGlobalMouseUp(event: MouseEvent) {
		if (this.isDragging) {
			this.onCellDragEnd(event);
		}
	}

	ngAfterViewInit(): void {
		const timeElement = document.getElementById('time-08:00');
		if (timeElement) {
			timeElement.scrollIntoView({ behavior: 'instant', block: 'start' });
		}
	}

	public isTimeSlotSelected(dayIndex: number, timeID: string, removedSchedule?: string): boolean {
		if (removedSchedule) {
			return (
				this.selectedSchedules()
					.filter((x) => x !== removedSchedule)
					.find((ss) => ss === `${dayIndex}-${timeID}`) !== undefined
			);
		} else {
			return this.selectedSchedules().find((ss) => ss === `${dayIndex}-${timeID}`) !== undefined;
		}
	}

	public isPreferredTimeSlot(dayIndex: number, timeID: string): boolean {
		if (this.formStep() !== 3) {
			return false;
		}

		return this.preferredTimeSlots()?.find((s) => s.dayIndex === dayIndex && s.timeID === timeID) !== undefined;
	}

	public daySelected(dayIndex: number): boolean {
		// Is Preferred Time Slot selection step
		if (this.formStep() === 2) {
			return this.selectedSchedules().some((ss) => {
				const [day] = ss.split('-');
				return Number(day) === dayIndex;
			});
		}
		return false;
	}

	public onCellDragStart(event: MouseEvent, dayIndex: number, timeID: string) {
		event.preventDefault();
		this.isDragging = true;
		const alreadySelected = this.isTimeSlotSelected(dayIndex, timeID);
		this.dragMode = alreadySelected ? 'remove' : 'add';
		this.dragCells.clear();
		this.processDragCell(dayIndex, timeID);
	}

	public onCellDragExit(event: MouseEvent, dayIndex: number, timeID: string) {
		if (!this.isDragging) {
			return;
		}

		this.onScheduleSelection(dayIndex, timeID);
	}

	public onCellDragEnter(event: MouseEvent, dayIndex: number, timeID: string) {
		if (!this.isDragging) return;
		this.processDragCell(dayIndex, timeID);
	}

	public onCellDragEnd(_: MouseEvent) {
		this.isDragging = false;
		this.dragCells.clear();
		this.dragMode = null;
	}

	public isCellInDrag(dayIndex: number, timeID: string): boolean {
		return this.dragCells.has(`${dayIndex}-${timeID}`);
	}

	public onScheduleSelection(dayIndex: number, timeID: string): void {
		if (this.formStep() === 3 && this.isPreferredTimeSlot(dayIndex, timeID)) {
			// In step 3 we cannot remove preferred time slots
			return;
		}

		if (this.dragMode === 'add' && !this.isTimeSlotSelected(dayIndex, timeID)) {
			this.addSelectedTimeSlot(dayIndex, timeID);
		}

		if (this.dragMode === 'remove' && this.isTimeSlotSelected(dayIndex, timeID)) {
			this.removeTimeSlot(dayIndex, timeID);
		}
	}

	private removeTimeSlot(dayIndex: number, timeID: string) {
		const removedSchedule = `${dayIndex}-${timeID}`;
		this.removeScheduleSelection.emit({ scheduleID: removedSchedule, step: this.formStep() });

		// Check if this creates a 30 min gap if so remove the adjacent 30 min slot
		const nextTimeID = this.getNextTimeID(timeID);
		const previousTimeID = this.getPreviousTimeID(timeID);

		if (nextTimeID && this.isIsolatedHalfHourSlot(dayIndex, nextTimeID, removedSchedule)) {
			this.removeScheduleSelection.emit({ scheduleID: `${dayIndex}-${nextTimeID}`, step: this.formStep() });
		}

		if (previousTimeID && this.isIsolatedHalfHourSlot(dayIndex, previousTimeID, removedSchedule)) {
			this.removeScheduleSelection.emit({ scheduleID: `${dayIndex}-${previousTimeID}`, step: this.formStep() });
		}
	}

	private addSelectedTimeSlot(dayIndex: number, timeID: string) {
		if (this.disableCalendar() || this.daySelected(dayIndex)) {
			return;
		}

		this.addScheduleSelection.emit({ scheduleData: { dayIndex: dayIndex, timeID }, step: this.formStep() });

		// Check if this is a 30 min time slot if so add the next 30 min slot
		const nextTimeID = this.getNextTimeID(timeID);
		const previousTimeID = this.getPreviousTimeID(timeID);

		if (nextTimeID && !this.isTimeSlotSelected(dayIndex, nextTimeID) && previousTimeID && !this.isTimeSlotSelected(dayIndex, previousTimeID)) {
			this.addScheduleSelection.emit({ scheduleData: { dayIndex: dayIndex, timeID: nextTimeID }, step: this.formStep() });
		} else if (nextTimeID == null && previousTimeID && !this.isTimeSlotSelected(dayIndex, previousTimeID)) {
			this.addScheduleSelection.emit({ scheduleData: { dayIndex: dayIndex, timeID: previousTimeID }, step: this.formStep() });
		}
	}

	private getNextTimeID(currentTimeID: string): string | null {
		const times = this.timesOfDay();
		const currentIndex = times.findIndex((t) => t.id === currentTimeID);
		return times[currentIndex + 1]?.id || null;
	}

	private getPreviousTimeID(currentTimeID: string): string | null {
		const times = this.timesOfDay();
		const currentIndex = times.findIndex((t) => t.id === currentTimeID);
		return times[currentIndex - 1]?.id || null;
	}

	private isIsolatedHalfHourSlot(dayIndex: number, timeID: string, removedSchedule: string): boolean {
		const nextTimeID = this.getNextTimeID(timeID);
		const previousTimeID = this.getPreviousTimeID(timeID);

		const isNextTimeSelected = nextTimeID ? this.isTimeSlotSelected(dayIndex, nextTimeID, removedSchedule) : false;
		const isPreviousTimeSelected = previousTimeID ? this.isTimeSlotSelected(dayIndex, previousTimeID, removedSchedule) : false;

		return !isNextTimeSelected && !isPreviousTimeSelected;
	}

	private populateTimes(timeIncrement: number = 30): { id: string; hour: number; minute: number; label: string }[] {
		const times = getTimeOptions(0, 24, timeIncrement, false, true, (rowCount: number) => {
			setCssValue(this._elementRef.nativeElement, '--calendar-week-view-rows', `${rowCount}`);
		});

		return times;
	}

	private processDragCell(dayIndex: number, timeID: string) {
		const key = `${dayIndex}-${timeID}`;
		if (this.dragCells.has(key)) return; // Already processed in this drag
		this.dragCells.add(key);
		this.onScheduleSelection(dayIndex, timeID);
	}
}
